/**
 * Menu management.
 *
 * The fiscal fields are the reason this screen is not just a price list. An
 * item without an HS code, a tax rate and a sale type will sell fine on the
 * till and be rejected by FBR or PRA hours later, so the form groups those
 * together, explains what they are for, and the list flags anything incomplete
 * before it becomes a rejected invoice.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FiPlus, FiSearch, FiEdit2, FiAlertTriangle, FiEyeOff, FiEye,
    FiBookOpen, FiLayers, FiTag
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { menuApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge, StatusBadge } from '../components/ui/Feedback';
import { Field, Input, NumberInput, Select, Textarea, Checkbox } from '../components/ui/Field';
import ImagePicker from '../components/ui/ImagePicker';
import { selectLookups, loadMenu } from '../store/menuSlice';
import { money } from '../utils/format';
import { assetUrl } from '../services/api';

const emptyItem = {
    itemCode: '', barcode: '', itemName: '', itemNameUrdu: '', itemDescription: '',
    categoryId: '', itemTypeId: '', kitchenSectionId: '',
    salePrice: 0, costPrice: 0, priceIncludesTax: true, maxDiscountPercent: 100, isOpenPrice: false,
    hsCodeId: '', salesTaxRateId: '', saleTypeId: '', uomId: '', fbrUomId: '',
    fbrQuantityRatio: 1, areSaleAndFbrUnitsSame: true, fixedRetailPrice: 0,
    imagePath: null, tileColour: '', isDeal: false, tracksInventory: true, isAvailable: true,
    preparationMinutes: '', displaySequence: 0
};

const MenuItemsPage = () => {
    const dispatch = useDispatch();
    const lookups = useSelector(selectLookups);

    const [filters, setFilters] = useState({ search: '', categoryId: '' });
    const [editing, setEditing] = useState(null);
    const [variantsFor, setVariantsFor] = useState(null);

    const loadItems = useCallback(() => menuApi.listItems(filters), [filters]);
    const { data: items, loading, refetch } = useAsync(loadItems, [filters]);

    const loadCategories = useCallback(() => menuApi.listCategories(), []);
    const { data: categories } = useAsync(loadCategories, []);

    const refreshAll = () => {
        refetch();
        // The till holds the menu in memory, so it has to be told to reload.
        dispatch(loadMenu());
    };

    const { run: saveItem, busy: saving } = useAction(async (form) => {
        const payload = {
            ...form,
            // No barcode is null, not an empty code another item could "share".
            barcode: form.barcode.trim() || null,
            categoryId: Number(form.categoryId),
            itemTypeId: Number(form.itemTypeId),
            kitchenSectionId: form.kitchenSectionId ? Number(form.kitchenSectionId) : null,
            hsCodeId: form.hsCodeId ? Number(form.hsCodeId) : null,
            salesTaxRateId: form.salesTaxRateId ? Number(form.salesTaxRateId) : null,
            saleTypeId: form.saleTypeId ? Number(form.saleTypeId) : null,
            uomId: form.uomId ? Number(form.uomId) : null,
            fbrUomId: form.fbrUomId ? Number(form.fbrUomId) : null,
            preparationMinutes: form.preparationMinutes ? Number(form.preparationMinutes) : null,
            salePrice: Number(form.salePrice),
            costPrice: Number(form.costPrice),
            maxDiscountPercent: Number(form.maxDiscountPercent),
            fbrQuantityRatio: Number(form.fbrQuantityRatio) || 1,
            fixedRetailPrice: Number(form.fixedRetailPrice) || 0,
            displaySequence: Number(form.displaySequence) || 0,
            // null clears the column; '' would fail the max(500) string check
            // and an empty path is not a path anyway.
            imagePath: form.imagePath || null
        };

        return form.Menu_Item_ID
            ? menuApi.updateItem(form.Menu_Item_ID, payload)
            : menuApi.createItem(payload);
    }, {
        successMessage: 'Menu item saved.',
        onSuccess: () => { setEditing(null); refreshAll(); }
    });

    const { run: toggleAvailability } = useAction(
        (id, available) => menuApi.setAvailability(id, available),
        { onSuccess: refreshAll }
    );

    const openNew = () => setEditing({
        ...emptyItem,
        categoryId: categories?.[0]?.Category_ID || '',
        itemTypeId: lookups.itemTypes.find((t) => t.Item_Type_Description === 'Services')?.Item_Type_ID
            || lookups.itemTypes[0]?.Item_Type_ID || '',
        // Pre-fill the fiscal fields with the defaults a restaurant uses, so a
        // new dish is compliant without anyone having to know what an HS code is.
        hsCodeId: lookups.hsCodes.find((h) => h.HS_Code === '9801.1000')?.HS_Code_ID || '',
        salesTaxRateId: lookups.taxRates.find((r) => r.Is_Default)?.Sales_Tax_Rate_ID
            || lookups.taxRates[0]?.Sales_Tax_Rate_ID || '',
        saleTypeId: lookups.saleTypes.find((s) => s.Is_Service)?.Sale_Type_ID
            || lookups.saleTypes[0]?.Sale_Type_ID || '',
        uomId: lookups.uoms.find((u) => u.UOM_Description === 'Plate')?.UOM_ID
            || lookups.uoms[0]?.UOM_ID || ''
    });

    const openEdit = (row) => setEditing({
        Menu_Item_ID: row.Menu_Item_ID,
        itemCode: row.Item_Code || '',
        barcode: row.Barcode || '',
        itemName: row.Item_Name,
        itemNameUrdu: row.Item_Name_Urdu || '',
        itemDescription: row.Item_Description || '',
        categoryId: row.Category_ID,
        itemTypeId: row.Item_Type_ID,
        kitchenSectionId: row.Kitchen_Section_ID || '',
        salePrice: Number(row.Sale_Price),
        costPrice: Number(row.Cost_Price),
        priceIncludesTax: !!row.Price_Includes_Tax,
        maxDiscountPercent: Number(row.Max_Discount_Percent),
        isOpenPrice: !!row.Is_Open_Price,
        hsCodeId: row.HS_Code_ID || '',
        salesTaxRateId: row.Sales_Tax_Rate_ID || '',
        saleTypeId: row.Sale_Type_ID || '',
        uomId: row.UOM_ID || '',
        fbrUomId: row.FBR_UOM_ID || '',
        fbrQuantityRatio: Number(row.FBR_Quantity_Ratio) || 1,
        areSaleAndFbrUnitsSame: !!row.Are_Sale_And_FBR_Units_Same,
        fixedRetailPrice: Number(row.Fixed_Retail_Price) || 0,
        imagePath: row.Image_Path || null,
        tileColour: row.Tile_Colour || '',
        isDeal: !!row.Is_Deal,
        tracksInventory: !!row.Tracks_Inventory,
        isAvailable: !!row.Is_Available,
        preparationMinutes: row.Preparation_Minutes || '',
        displaySequence: row.Display_Sequence || 0
    });

    /**
     * The barcode scanner.
     *
     * With the form open, a scan fills the Barcode field whichever field has
     * focus - the hook has already taken the characters back out of it - and
     * warns straight away if another item has that code. On the list, a scan
     * finds the product: it opens the item carrying the code, or starts a new
     * item with the barcode filled in, which is the quick way to add stock that
     * arrived with a barcode printed on it.
     *
     * Off while the sizes editor is open; that takes scans for its own rows.
     */
    useBarcodeScanner(async (code) => {
        const sameCode = (value) => (value || '').toLowerCase() === code.toLowerCase();

        if (editing) setEditing((current) => current && { ...current, barcode: code });

        let matches;
        try {
            // The list search matches a barcode exactly, and needs no permission
            // beyond this screen's own.
            matches = await menuApi.listItems({ search: code });
        } catch (err) {
            toast.error(err.message, { id: 'scan' });
            return;
        }
        const owner = matches.find((row) => sameCode(row.Barcode))
            || (!editing && matches.find((row) => sameCode(row.Item_Code)));

        if (editing) {
            if (owner && owner.Menu_Item_ID !== editing.Menu_Item_ID) {
                toast.error(`Barcode ${code} is already on ${owner.Item_Name}.`, { id: 'scan' });
            } else {
                toast.success(`Barcode ${code} scanned.`, { id: 'scan' });
            }
            return;
        }

        if (owner) {
            openEdit(owner);
            return;
        }
        openNew();
        setEditing((current) => ({ ...current, barcode: code }));
        toast.success(`Nothing has barcode ${code} yet - add it as a new item.`, { id: 'scan' });
    }, { enabled: !variantsFor });

    const columns = [
        {
            key: 'Item_Name',
            header: 'Item',
            render: (row) => (
                <div className="flex items-center gap-2">
                    <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 text-[10px] font-bold text-slate-500"
                        style={!row.Image_Path && row.Tile_Colour
                            ? { backgroundColor: `${row.Tile_Colour}22`, color: row.Tile_Colour }
                            : undefined}
                    >
                        {row.Image_Path
                            ? <img src={assetUrl(row.Image_Path)} alt="" className="h-full w-full object-cover" loading="lazy" />
                            : row.Item_Name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <p className="truncate font-medium text-slate-800">
                            {row.Item_Name}
                            {row.Is_Deal && <Badge tone="amber" className="ml-2">deal</Badge>}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                            {row.Category_Name}
                            {row.Item_Code && ` · ${row.Item_Code}`}
                        </p>
                    </div>
                </div>
            )
        },
        {
            key: 'Sale_Price',
            header: 'Price',
            align: 'right',
            render: (row) => (
                <div>
                    <p className="font-medium">{money(row.Sale_Price)}</p>
                    {row.Cost_Price > 0 && (
                        <p className="text-xs text-slate-500">cost {money(row.Cost_Price)}</p>
                    )}
                </div>
            )
        },
        {
            key: 'tax',
            header: 'Tax',
            render: (row) => (
                row.Sales_Tax_Rate_Label
                    ? <span className="text-slate-700">{row.Sales_Tax_Rate_Label}</span>
                    : <Badge tone="red">not set</Badge>
            )
        },
        {
            key: 'HS_Code',
            header: 'HS code',
            render: (row) => (
                row.HS_Code
                    ? <span className="tabular text-xs text-slate-600">{row.HS_Code}</span>
                    : <Badge tone="red">missing</Badge>
            )
        },
        {
            key: 'fiscal',
            header: 'Ready to report',
            render: (row) => {
                const ready = row.HS_Code && row.Sales_Tax_Rate_ID && row.Sale_Type_ID;
                return ready
                    ? <Badge tone="green">yes</Badge>
                    : (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                            <FiAlertTriangle className="h-3.5 w-3.5" />
                            would be rejected
                        </span>
                    );
            }
        },
        {
            key: 'Is_Available',
            header: 'On sale',
            render: (row) => <StatusBadge status={row.Is_Available ? 'OK' : 'OUT_OF_STOCK'}
                label={row.Is_Available ? 'available' : 'sold out'} />
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end gap-1">
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleAvailability(row.Menu_Item_ID, !row.Is_Available);
                        }}
                        title={row.Is_Available ? 'Mark sold out' : 'Put back on sale'}
                    >
                        {row.Is_Available ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={(event) => { event.stopPropagation(); openEdit(row); }}
                    >
                        <FiEdit2 className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Menu Items"
                subtitle="What the till sells, and how each item is reported"
                actions={(
                    <button type="button" className="btn-primary" onClick={openNew}>
                        <FiPlus className="h-4 w-4" /> New item
                    </button>
                )}
            />

            <div className="toolbar">
                <div className="relative w-full sm:min-w-[16rem] sm:flex-1">
                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        className="input pl-9"
                        placeholder="Search by name or code, or scan a barcode"
                        value={filters.search}
                        onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                    />
                </div>

                <select
                    className="input w-full sm:w-52"
                    value={filters.categoryId}
                    onChange={(event) => setFilters({ ...filters, categoryId: event.target.value })}
                >
                    <option value="">All categories</option>
                    {(categories || []).map((category) => (
                        <option key={category.Category_ID} value={category.Category_ID}>
                            {category.Category_Name}
                        </option>
                    ))}
                </select>
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={items || []}
                loading={loading}
                rowKey={(row) => row.Menu_Item_ID}
                onRowClick={openEdit}
                emptyTitle="No menu items"
                emptyDescription="Add the first one to start selling."
                emptyIcon={FiBookOpen}
            />

            {/* The editor */}
            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing?.Menu_Item_ID ? 'Edit item' : 'New item'}
                width="xl"
                dismissible={!saving}
                footer={(
                    <>
                        {editing?.Menu_Item_ID && (
                            <button
                                type="button"
                                className="btn-secondary mr-auto"
                                onClick={() => setVariantsFor(editing)}
                            >
                                <FiLayers /> Sizes
                            </button>
                        )}
                        <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={saving || !editing?.itemName || !editing?.categoryId}
                            onClick={() => saveItem(editing)}
                        >
                            {saving ? 'Saving…' : 'Save item'}
                        </button>
                    </>
                )}
            >
                {editing && (
                    <div className="space-y-6">
                        {/* What it is */}
                        <section className="grid gap-3 sm:grid-cols-2">
                            <Field label="Name" required className="sm:col-span-2">
                                <Input
                                    value={editing.itemName}
                                    onChange={(e) => setEditing({ ...editing, itemName: e.target.value })}
                                    data-autofocus
                                />
                            </Field>

                            <Field label="Category" required>
                                <Select
                                    value={editing.categoryId}
                                    onChange={(e) => setEditing({ ...editing, categoryId: e.target.value })}
                                >
                                    <option value="">Choose…</option>
                                    {(categories || []).map((c) => (
                                        <option key={c.Category_ID} value={c.Category_ID}>{c.Category_Name}</option>
                                    ))}
                                </Select>
                            </Field>

                            <Field label="Kitchen section" hint="Where its ticket prints.">
                                <Select
                                    value={editing.kitchenSectionId}
                                    onChange={(e) => setEditing({ ...editing, kitchenSectionId: e.target.value })}
                                >
                                    <option value="">Use the category default</option>
                                    {lookups.kitchenSections.map((s) => (
                                        <option key={s.Kitchen_Section_ID} value={s.Kitchen_Section_ID}>
                                            {s.Kitchen_Section_Name}
                                        </option>
                                    ))}
                                </Select>
                            </Field>

                            <Field label="Short code" hint="Typed on the till to find the item fast.">
                                <Input
                                    value={editing.itemCode}
                                    onChange={(e) => setEditing({ ...editing, itemCode: e.target.value })}
                                />
                            </Field>

                            <Field label="Barcode" hint="Scan it from anywhere on this form, or type it.">
                                <Input
                                    value={editing.barcode}
                                    onChange={(e) => setEditing({ ...editing, barcode: e.target.value })}
                                    placeholder="Scan or type"
                                    autoComplete="off"
                                />
                            </Field>

                            <Field label="Photo" className="sm:col-span-2">
                                <ImagePicker
                                    value={editing.imagePath}
                                    onChange={(imagePath) => setEditing({ ...editing, imagePath })}
                                />
                            </Field>
                        </section>

                        {/* Money */}
                        <section>
                            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                                <FiTag className="h-4 w-4" /> Pricing
                            </h3>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <Field label="Sale price" required>
                                    <NumberInput
                                        value={editing.salePrice}
                                        onChange={(e) => setEditing({ ...editing, salePrice: e.target.value })}
                                    />
                                </Field>
                                <Field label="Cost price" hint="Used for the margin report.">
                                    <NumberInput
                                        value={editing.costPrice}
                                        onChange={(e) => setEditing({ ...editing, costPrice: e.target.value })}
                                    />
                                </Field>
                                <Field label="Maximum discount %">
                                    <NumberInput
                                        value={editing.maxDiscountPercent}
                                        onChange={(e) => setEditing({ ...editing, maxDiscountPercent: e.target.value })}
                                    />
                                </Field>
                            </div>

                            <div className="mt-3 space-y-2">
                                <Checkbox
                                    label="The price includes sales tax"
                                    description="Normal for a Pakistani menu - the tax is worked backwards out of the price."
                                    checked={editing.priceIncludesTax}
                                    onChange={(v) => setEditing({ ...editing, priceIncludesTax: v })}
                                />
                                <Checkbox
                                    label="Open price"
                                    description="The cashier types the amount. For a custom cake or a service charge."
                                    checked={editing.isOpenPrice}
                                    onChange={(v) => setEditing({ ...editing, isOpenPrice: v })}
                                />
                            </div>
                        </section>

                        {/* Fiscal */}
                        <section className="rounded-xl bg-slate-50 p-4">
                            <h3 className="mb-1 text-sm font-semibold text-slate-700">
                                Reporting to FBR / PRA
                            </h3>
                            <p className="mb-3 text-xs text-slate-500">
                                An item missing any of these will sell on the till and then be rejected
                                by the authority. The defaults suit a restaurant.
                            </p>

                            <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="HS code" required>
                                    <Select
                                        value={editing.hsCodeId}
                                        onChange={(e) => setEditing({ ...editing, hsCodeId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {lookups.hsCodes.map((h) => (
                                            <option key={h.HS_Code_ID} value={h.HS_Code_ID}>
                                                {h.HS_Code} — {h.HS_Code_Description}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Sales tax rate" required>
                                    <Select
                                        value={editing.salesTaxRateId}
                                        onChange={(e) => setEditing({ ...editing, salesTaxRateId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {lookups.taxRates.map((r) => (
                                            <option key={r.Sales_Tax_Rate_ID} value={r.Sales_Tax_Rate_ID}>
                                                {r.Sales_Tax_Rate_DES}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Sale type / scenario" required>
                                    <Select
                                        value={editing.saleTypeId}
                                        onChange={(e) => setEditing({ ...editing, saleTypeId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {lookups.saleTypes.map((s) => (
                                            <option key={s.Sale_Type_ID} value={s.Sale_Type_ID}>
                                                {s.Sale_Type_Description} ({s.FBR_Scenario_ID})
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Item type" required>
                                    <Select
                                        value={editing.itemTypeId}
                                        onChange={(e) => setEditing({ ...editing, itemTypeId: e.target.value })}
                                    >
                                        {lookups.itemTypes.map((t) => (
                                            <option key={t.Item_Type_ID} value={t.Item_Type_ID}>
                                                {t.Item_Type_Description}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Sold in" hint="The unit on the bill.">
                                    <Select
                                        value={editing.uomId}
                                        onChange={(e) => setEditing({ ...editing, uomId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {lookups.uoms.filter((u) => u.Is_Sale_Unit).map((u) => (
                                            <option key={u.UOM_ID} value={u.UOM_ID}>{u.UOM_Description}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field
                                    label="Reported in"
                                    hint="Leave the same unless the authority wants a different one."
                                >
                                    <Select
                                        value={editing.fbrUomId}
                                        onChange={(e) => setEditing({ ...editing, fbrUomId: e.target.value })}
                                    >
                                        <option value="">Same as sold in</option>
                                        {lookups.uoms.map((u) => (
                                            <option key={u.UOM_ID} value={u.UOM_ID}>
                                                {u.UOM_Description} ({u.FBR_UOM_Label})
                                            </option>
                                        ))}
                                    </Select>
                                </Field>
                            </div>

                            {/* Only relevant when the two units differ */}
                            {editing.fbrUomId && String(editing.fbrUomId) !== String(editing.uomId) && (
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <Field
                                        label="Conversion ratio"
                                        hint="How many reported units one sold unit equals."
                                    >
                                        <NumberInput
                                            step="0.000001"
                                            value={editing.fbrQuantityRatio}
                                            onChange={(e) => setEditing({
                                                ...editing,
                                                fbrQuantityRatio: e.target.value,
                                                areSaleAndFbrUnitsSame: false
                                            })}
                                        />
                                    </Field>
                                </div>
                            )}
                        </section>

                        {/* Behaviour */}
                        <section className="space-y-2">
                            <Checkbox
                                label="Consumes stock"
                                description="Turn off for a service charge or anything with no ingredients."
                                checked={editing.tracksInventory}
                                onChange={(v) => setEditing({ ...editing, tracksInventory: v })}
                            />
                            <Checkbox
                                label="This is a deal"
                                description="Its contents are set up separately, under deal components."
                                checked={editing.isDeal}
                                onChange={(v) => setEditing({ ...editing, isDeal: v })}
                            />
                            <Checkbox
                                label="Available for sale"
                                checked={editing.isAvailable}
                                onChange={(v) => setEditing({ ...editing, isAvailable: v })}
                            />
                        </section>
                    </div>
                )}
            </Modal>

            <VariantsModal
                item={variantsFor}
                onClose={() => setVariantsFor(null)}
                onSaved={refreshAll}
            />
        </div>
    );
};

/* ------------------------------ sizes editor ------------------------------ */

const VariantsModal = ({ item, onClose, onSaved }) => {
    const [rows, setRows] = useState([]);

    // Fetched when the dialog opens rather than on mount: it is always rendered
    // but only occasionally used, and the sizes must be current when it is.
    useEffect(() => {
        if (!item?.Menu_Item_ID) {
            setRows([]);
            return;
        }

        let cancelled = false;
        menuApi.getItem(item.Menu_Item_ID)
            .then((detail) => {
                if (cancelled) return;
                setRows((detail.variants || []).map((v) => ({
                    variantName: v.Variant_Name,
                    // Carried through even when untouched: saving replaces every
                    // size, so a barcode left out here would be wiped.
                    barcode: v.Barcode || '',
                    salePrice: Number(v.Sale_Price ?? v.Effective_Price),
                    isDefault: !!v.Is_Default
                })));
            })
            .catch((err) => !cancelled && toast.error(err.message));

        return () => { cancelled = true; };
    }, [item]);

    const { run: save, busy } = useAction(
        () => menuApi.setVariants(
            item.Menu_Item_ID,
            rows.filter((r) => r.variantName.trim()).map((r) => ({ ...r, barcode: r.barcode.trim() || null }))
        ),
        { successMessage: 'Sizes saved.', onSuccess: () => { onSaved(); onClose(); } }
    );

    // A scan goes to the size whose barcode box has focus, otherwise to the
    // first size without a barcode - so a shelf of bottles can be scanned in
    // order without clicking between them.
    useBarcodeScanner((code, { target }) => {
        const focused = target?.dataset?.sizeBarcode;
        const index = focused !== undefined ? Number(focused) : rows.findIndex((r) => !r.barcode.trim());

        if (index < 0 || !rows[index]) {
            toast.error(rows.length
                ? 'Every size has a barcode. Click into the one to replace, then scan.'
                : 'Add a size first, then scan its barcode.', { id: 'scan' });
            return;
        }
        setRows((current) => current.map((r, i) => (i === index ? { ...r, barcode: code } : r)));
        toast.success(`Barcode ${code} added to ${rows[index].variantName || 'the size'}.`, { id: 'scan' });
    }, { enabled: !!item });

    return (
        <Modal
            open={!!item}
            onClose={onClose}
            title={`Sizes for ${item?.itemName || ''}`}
            subtitle="Small, Medium, Large - each with its own price, and its own barcode if it has one."
            width="md"
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn-primary" onClick={() => save()} disabled={busy}>
                        {busy ? 'Saving…' : 'Save sizes'}
                    </button>
                </>
            )}
        >
            <div className="space-y-2">
                {rows.map((row, index) => (
                    <div key={index} className="flex items-end gap-2">
                        <Field label={index === 0 ? 'Name' : null} className="flex-1">
                            <Input
                                value={row.variantName}
                                onChange={(e) => setRows(rows.map((r, i) =>
                                    (i === index ? { ...r, variantName: e.target.value } : r)))}
                                placeholder="Medium"
                            />
                        </Field>
                        <Field label={index === 0 ? 'Barcode' : null} className="w-28 sm:w-40">
                            <Input
                                value={row.barcode}
                                onChange={(e) => setRows(rows.map((r, i) =>
                                    (i === index ? { ...r, barcode: e.target.value } : r)))}
                                placeholder="Scan"
                                autoComplete="off"
                                data-size-barcode={index}
                            />
                        </Field>
                        <Field label={index === 0 ? 'Price' : null} className="w-24 sm:w-32">
                            <NumberInput
                                value={row.salePrice}
                                onChange={(e) => setRows(rows.map((r, i) =>
                                    (i === index ? { ...r, salePrice: Number(e.target.value) } : r)))}
                            />
                        </Field>
                        <button
                            type="button"
                            className="btn-ghost btn-sm mb-1 !px-2 text-red-600"
                            onClick={() => setRows(rows.filter((_, i) => i !== index))}
                        >
                            Remove
                        </button>
                    </div>
                ))}

                <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setRows([...rows, {
                        variantName: '', barcode: '', salePrice: 0, isDefault: rows.length === 0
                    }])}
                >
                    <FiPlus className="h-3.5 w-3.5" /> Add a size
                </button>
            </div>
        </Modal>
    );
};

export default MenuItemsPage;
